import { z } from 'zod';
import type {} from '../../libs/augmentations/zod.d.ts';
import * as R from 'remeda';

import {
  DateTimeSchema,
  objectIdRef,
  ObjectIdSchema,
  ObjectIdString,
  SimpleObjectIdString,
  subDocument,
} from '../common.js';
import { AccountDTO } from './accountSchema.js';
import {
  ActivityDataMappingConfig,
  ChromeExtensionConfig,
  DataIQConfig,
  TalentIQConfig,
} from './configSchemas.js';
import { StaffIQConfig } from './staffIqConfigSchema.js';
import { RelatedEntityMetadataMap } from '../../interfaces.js';
import {
  IntegrationSecretSchemaName,
  MSDynamicsCredentialsInput,
  MSDynamicsCredentialsResponse,
  SalesforceCredentialsInput,
  SalesforceCredentialsResponse,
} from './integrationSecretSchemas.js';

// ------------------------------------------------------------------------------------------------------------------ //
//region IntegrationConfig schemas

export const SsnRedactionMode = z.enum(['redact', 'keepLast4', 'preserve']);
export type SsnRedactionMode = z.infer<typeof SsnRedactionMode>;

export const RedactionConfig = z.object({
  ssnRedaction: SsnRedactionMode.optional(),
  // Per entity, the ATS fields no Toro product reads or writes, e.g. { Candidate: ['customText11'] }
  redactedFields: z.record(z.string(), z.string().array()).optional(),
});
export type RedactionConfig = z.infer<typeof RedactionConfig>;

export const NlpConfig = z.object({
  spanishFalsePositiveFiltering: z.boolean().default(false),
});
export type NlpConfig = z.infer<typeof NlpConfig>;

export const AnalyticsConfig = z.object({
  enabled: z.boolean().nullish(),
});
export type AnalyticsConfig = z.infer<typeof AnalyticsConfig>;

export const IntegrationConfig = z.object({
  preloadNotes: z.boolean().default(true),
  /** @deprecated */
  preloadInsights: z.boolean().default(false).meta({ deprecated: true }),
  preloadJobs: z.boolean().default(true),
  preloadJobProfiles: z.boolean().default(false),
  redactionConfig: RedactionConfig,
  nlpConfig: NlpConfig,
  talentIqConfig: TalentIQConfig,
  dataIqConfig: DataIQConfig,
  activityDataMappings: ActivityDataMappingConfig.array(),
  staffIqConfig: StaffIQConfig,
  analyticsConfig: AnalyticsConfig.nullish(),
  chromeExtensionConfig: ChromeExtensionConfig.optional(),
});
export type IntegrationConfig = z.infer<typeof IntegrationConfig>;

export const SimpleConfig = IntegrationConfig.pick({
  preloadNotes: true,
  preloadInsights: true,
  preloadJobs: true,
  preloadJobProfiles: true,
});
export type SimpleConfig = z.infer<typeof SimpleConfig>;

// SimpleConfig with all defaults removed
export const SimpleConfigUpdate = SimpleConfig.required().partial();
export type SimpleConfigUpdate = z.infer<typeof SimpleConfigUpdate>;

//endregion
// ------------------------------------------------------------------------------------------------------------------ //
//region Base Integration types
export const AtsType = z.enum(['bullhorn', 'microsoftDynamics', 'salesforce']);
export type AtsType = z.infer<typeof AtsType>;

export const NonAtsIntegrationType = z.enum(['textUs']);
export type NonAtsIntegrationType = z.infer<typeof NonAtsIntegrationType>;

export const IntegrationType = z.enum([...AtsType.options, ...NonAtsIntegrationType.options]);
export type IntegrationType = z.infer<typeof IntegrationType>;

export const IntegrationStatus = z.enum(['active', 'disabled', 'terminated', 'deleted']);
export type IntegrationStatus = z.infer<typeof IntegrationStatus>;

export const INTEGRATION_TO_SECRET_NAME = {
  bullhorn: 'bullhornIntegrationSecret',
  textUs: 'textUsIntegrationSecret',
  microsoftDynamics: 'microsoftDynamicsSecret',
  salesforce: 'salesforceSecret',
} as const satisfies Record<IntegrationType, IntegrationSecretSchemaName>;
export type SecretNameForIntegration<T extends IntegrationType> =
  (typeof INTEGRATION_TO_SECRET_NAME)[T];

export const SecretMetadata = z.object({
  secretName: z.string(),
  version: z.string(),
  schemaName: IntegrationSecretSchemaName,
  schemaVersion: z.number().int(),
  tags: z.record(z.string(), z.string()),
  updatedAt: DateTimeSchema,
});
export interface SecretMetadata<
  T extends IntegrationSecretSchemaName = IntegrationSecretSchemaName,
> extends z.infer<typeof SecretMetadata> {
  schemaName: T;
}

export const IIntegration = z.object({
  _id: ObjectIdSchema,
  name: z.string().optional(),
  account: objectIdRef('Account'),
  status: IntegrationStatus.default('active'),
  type: IntegrationType,
  apiData: z.record(z.string(), z.unknown()),
  config: IntegrationConfig.partial().nullish(),
  secretMetadata: subDocument(SecretMetadata).nullish(),
  metadata: z.object({
    relatedEntities: RelatedEntityMetadataMap,
  }),
  createdAt: DateTimeSchema,
  updatedAt: DateTimeSchema,
});
export type IIntegration = z.infer<typeof IIntegration>;

//endregion
// ------------------------------------------------------------------------------------------------------------------ //
//region Bullhorn Integrations

export const BullhornSessionData = z.object({
  redirectUri: z.string().nullish(),
  oauthUrl: z.string().nullish(),
  userId: z.string().nullish(),
  restServices: z.string().nullish(),
  restUrl: z.string().nullish(),
  bhRestToken: z.string().nullish(),
  refreshToken: z.string().nullish(),
});
export type BullhornSessionData = z.infer<typeof BullhornSessionData>;

export const BullhornIntegrationApiData = z.object({
  enableBullhornInternalApi: z.boolean().default(false),
  internalApiEmail: z.email().trim().toLowerCase().or(z.literal('')).nullish().default(''),
  authUrl: z.string().nullish().default(''),
  restUrl: z.string().nullish().default(''),
  redirectUri: z.string().nullish().default('https://toroinsights.ai'),
  swimlane: z.string().nullish().default(''),
  corpId: z.string().nullish().default(''),
  sessionData: BullhornSessionData.default({}),
  internalSessionData: BullhornSessionData.default({}),
  entityFieldExclusions: z.record(z.string(), z.string().array()).default({}),
});
export type BullhornIntegrationApiData = z.infer<typeof BullhornIntegrationApiData>;

export const BullhornIntegrationApiDataInput = BullhornIntegrationApiData.required().partial();

export const IBullhornIntegration = IIntegration.safeExtend({
  type: z.literal('bullhorn'),
  apiData: BullhornIntegrationApiData,
  config: IntegrationConfig,
  metadata: z.object({
    relatedEntities: RelatedEntityMetadataMap,
  }),
  secretMetadata: subDocument(
    SecretMetadata.safeExtend({ schemaName: z.literal('bullhornIntegrationSecret') })
  ).nullish(),
});
export type IBullhornIntegration = z.infer<typeof IBullhornIntegration>;

//endregion
// ------------------------------------------------------------------------------------------------------------------ //
//region Microsoft Dynamics Integrations

export const MSDynamicsIntegrationApiData = z.object({
  apiUrl: z.httpUrl({ normalize: true }).nullish(),
  tenant: z.guid().nullish(),
  sessionData: subDocument(
    z.object({
      sessionToken: z.string().nullish(),
      expiresAt: DateTimeSchema.nullish(),
    })
  ).nullish(),
});
export type MSDynamicsIntegrationApiData = z.infer<typeof MSDynamicsIntegrationApiData>;

export const IMicrosoftDynamicsIntegration = IIntegration.safeExtend({
  type: z.literal('microsoftDynamics'),
  apiData: MSDynamicsIntegrationApiData,
  // config: z.object({}),
  secretMetadata: subDocument(
    SecretMetadata.safeExtend({ schemaName: z.literal('microsoftDynamicsSecret') })
  ).nullish(),
});
export type IMicrosoftDynamicsIntegration = z.infer<typeof IMicrosoftDynamicsIntegration>;

export const MSDynamicsApiDataInput = MSDynamicsIntegrationApiData.omit({ sessionData: true })
  .required()
  .partial();
export const MSDynamicsUpdateApiConfigInput = z
  .object({ apiData: MSDynamicsApiDataInput, credentials: MSDynamicsCredentialsInput })
  .partial();
export type MSDynamicsUpdateApiConfigInput = z.infer<typeof MSDynamicsUpdateApiConfigInput>;

//endregion
// ------------------------------------------------------------------------------------------------------------------ //
//region Salesforce Integrations

export const SalesforceIntegrationApiData = z.object({
  loginUrl: z.httpUrl({ normalize: true }).nullish(),
  // Salesforce reports no expiry, so a 401 is the refresh signal
  sessionData: subDocument(
    z.object({
      accessToken: z.string().nullish(),
      runAsUserId: z.string().nullish(),
    })
  ).nullish(),
});
export type SalesforceIntegrationApiData = z.infer<typeof SalesforceIntegrationApiData>;

export const ISalesforceIntegration = IIntegration.safeExtend({
  type: z.literal('salesforce'),
  apiData: SalesforceIntegrationApiData,
  secretMetadata: subDocument(
    SecretMetadata.safeExtend({ schemaName: z.literal('salesforceSecret') })
  ).nullish(),
});
export type ISalesforceIntegration = z.infer<typeof ISalesforceIntegration>;

export const SalesforceApiDataInput = SalesforceIntegrationApiData.omit({ sessionData: true })
  .required()
  .partial();
export const SalesforceUpdateApiConfigInput = z
  .object({ apiData: SalesforceApiDataInput, credentials: SalesforceCredentialsInput })
  .partial();
export type SalesforceUpdateApiConfigInput = z.infer<typeof SalesforceUpdateApiConfigInput>;

//endregion
// ------------------------------------------------------------------------------------------------------------------ //
//region DTOs

export const IntegrationDTO = z.object({
  _id: ObjectIdString,
  account: AccountDTO.or(ObjectIdString),
  status: IntegrationStatus,
  type: IntegrationType,
  name: z.string().optional(),
  corpId: z.string().nullish(),
  apiUserId: z.string().nullish(),
  // Exclude activityDataMappings from the general DTO because they're huge and prone to errors if misconfigured
  config: IntegrationConfig.omit({ activityDataMappings: true }).partial().nullish(),
  createdAt: DateTimeSchema.optional(),
  updatedAt: DateTimeSchema.optional(),
  metadata: z.object({ relatedEntities: RelatedEntityMetadataMap }).partial().optional(),
});
export type IntegrationDTO = z.infer<typeof IntegrationDTO>;

export const PopulatedIntegrationDTO = IntegrationDTO.safeExtend({ account: AccountDTO });
export type PopulatedIntegrationDTO = z.infer<typeof PopulatedIntegrationDTO>;

export const IntegrationCreateSchema = z.object({
  accountId: SimpleObjectIdString,
  type: IntegrationType,
  status: IntegrationStatus.optional(),
  name: z.string().optional(),
});

// Labels for default integration names
export const INTEGRATION_NAME_LABELS = {
  bullhorn: 'Bullhorn',
  textUs: 'TextUs',
  microsoftDynamics: 'Dynamics',
  salesforce: 'Salesforce',
} as const satisfies Record<IntegrationType, string>;

//endregion
// ------------------------------------------------------------------------------------------------------------------ //

export const MSDynamicsCombinedApiConfig = z.object({
  apiData: MSDynamicsIntegrationApiData.prefault({}),
  credentials: MSDynamicsCredentialsResponse.prefault({}),
});
export type MSDynamicsCombinedApiConfig = z.infer<typeof MSDynamicsCombinedApiConfig>;

export const SalesforceCombinedApiConfig = z.object({
  apiData: SalesforceIntegrationApiData.prefault({}),
  credentials: SalesforceCredentialsResponse.prefault({}),
});
export type SalesforceCombinedApiConfig = z.infer<typeof SalesforceCombinedApiConfig>;
